import { useState } from 'react';
import { ListPage, type Column } from '../../components/ListPage';
import { useAuth } from '../../lib/auth';
import { can } from '../../lib/permissions';
import { api } from '../../lib/api';
import { dateLabel } from '../../lib/format';
import { Pill, Field, TextInput, TextArea, DateInput, FormGrid } from '../../components/ui';

// 售后返工：交货后客户不合格 → 退货返工（免费，原单据不变）
// POST /api/asset-return/customer-return · GET /api/asset-return/reworks?type=customer_return

type ReworkRow = {
  id: string;
  number?: string;
  reason?: string;
  status?: string;
  created_at?: string;
  original_delivery_number?: string;
  original_contract_number?: string;
  original_inspection_number?: string;
  customer_serial?: string;
  customer_name?: string;
  warranty?: number;
  chargeable?: number;
};

const STATUS: Record<string, { label: string; tone: 'ok' | 'info' | 'warn' }> = {
  draft: { label: '待返工', tone: 'warn' },
  in_progress: { label: '返工中', tone: 'info' },
  completed: { label: '已完成', tone: 'ok' },
};

const today = () => new Date().toISOString().slice(0, 10);

export function AfterSalesPage() {
  const { user } = useAuth();
  const canWrite = can(user, 'returns', 'create');
  const [creating, setCreating] = useState(false);
  const [reloadSignal, setReloadSignal] = useState(0);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({
    asset_id: '', original_delivery_id: '', original_inspection_id: '',
    serial_number: '', reason: '', intake_date: today(), notes: '',
  });
  const set = (k: string, v: string) => setF((s) => ({ ...s, [k]: v }));

  const submit = async () => {
    setErr('');
    if (!f.asset_id?.trim()) { setErr('请填写工件 ID 或序列号对应的工件'); return; }
    if (!f.original_delivery_id?.trim()) { setErr('请填写原交货单'); return; }
    if (!f.reason?.trim()) { setErr('返工原因为必填'); return; }
    setBusy(true);
    try {
      await api.post('/api/asset-return/customer-return', {
        asset_id: f.asset_id.trim(),
        original_delivery_id: f.original_delivery_id.trim(),
        original_inspection_id: f.original_inspection_id.trim() || undefined,
        reason: f.reason.trim(),
        intake_date: f.intake_date,
        notes: f.notes.trim() || undefined,
        warranty: true,
        chargeable: false,
        source_system: 'web-console',
        source_id: `CR/${f.original_delivery_id.trim()}/${Date.now()}`,
      });
      setCreating(false);
      setF({ asset_id: '', original_delivery_id: '', original_inspection_id: '', serial_number: '', reason: '', intake_date: today(), notes: '' });
      setReloadSignal((n) => n + 1);
    } catch (e: any) {
      setErr(e?.message || '创建失败');
    } finally {
      setBusy(false);
    }
  };

  const columns: Column<ReworkRow>[] = [
    { header: '返修单号', render: (r) => <span className="num strong">{r.number || r.id}</span> },
    { header: '客户', render: (r) => <span className="muted">{r.customer_name || '—'}</span> },
    { header: '序列号', render: (r) => <span className="muted">{r.customer_serial || '—'}</span> },
    { header: '原交货单', render: (r) => <span className="muted">{r.original_delivery_number || '—'}</span> },
    { header: '原合同', render: (r) => <span className="muted">{r.original_contract_number || '—'}</span> },
    { header: '原因', render: (r) => <span className="muted">{r.reason || '—'}</span> },
    { header: '日期', render: (r) => <span className="muted">{dateLabel(r.created_at)}</span> },
    {
      header: '状态',
      render: (r) => {
        const s = STATUS[r.status || 'draft'] || STATUS.draft;
        return <Pill tone={s.tone} label={s.label} />;
      },
    },
  ];

  return (
    <>
      {creating ? (
        <div className="panel" style={{ marginBottom: 12 }}>
          <div className="panel-head">
            <div>
              <h2>新建售后返工</h2>
              <div className="desc">交货后客户反馈不合格 → 退货返工（默认免费）。原订单/原交货保留；新维修周期 V+1；复检后再交货。</div>
            </div>
          </div>
          {err ? <div className="notice bad">{err}</div> : null}
          <FormGrid>
            <Field label="工件 ID *" required>
              <TextInput value={f.asset_id} onChange={(e) => set('asset_id', e.target.value)} placeholder="physical_asset.id（序列号查询可得）" />
            </Field>
            <Field label="原交货单 *" required>
              <TextInput value={f.original_delivery_id} onChange={(e) => set('original_delivery_id', e.target.value)} placeholder="REM-… 或交货单 ID" />
            </Field>
            <Field label="原质检单（可选）">
              <TextInput value={f.original_inspection_id} onChange={(e) => set('original_inspection_id', e.target.value)} placeholder="QC-…" />
            </Field>
            <Field label="到货日期">
              <DateInput value={f.intake_date} onChange={(e) => set('intake_date', e.target.value)} />
            </Field>
            <Field label="返工原因 *" required span>
              <TextArea value={f.reason} onChange={(e) => set('reason', e.target.value)} placeholder="客户反馈的不合格现象…" />
            </Field>
            <Field label="备注" span>
              <TextArea value={f.notes} onChange={(e) => set('notes', e.target.value)} />
            </Field>
          </FormGrid>
          <div className="detail-actions">
            <button className="btn primary" onClick={submit} disabled={busy}>{busy ? '提交中…' : '创建售后返工'}</button>
            <button className="btn" onClick={() => setCreating(false)}>取消</button>
          </div>
        </div>
      ) : null}
      <ListPage<ReworkRow>
        key={reloadSignal}
        title="售后返工"
        desc="交货后客户不合格 → 退货返工（免费）。按序列号/工件追原订单与原交货；新维修周期 V+1；复检后再交货。"
        endpoint="/api/asset-return/reworks?type=customer_return"
        columns={columns}
        rowId={(r) => String(r.id)}
        renderDetail={(r) => (
          <div className="k" style={{ fontSize: 12, color: 'var(--ink-3)', padding: '8px 0' }}>
            原交货 {r.original_delivery_number || '—'} · 原合同 {r.original_contract_number || '—'}
            {r.original_inspection_number ? ` · 原质检 ${r.original_inspection_number}` : ''}
            {' · '}{r.reason || ''}
          </div>
        )}
        showYear={false}
        actions={canWrite ? (
          <button className="btn primary" onClick={() => setCreating((v) => !v)}>{creating ? '收起新建' : '新建售后返工'}</button>
        ) : undefined}
      />
    </>
  );
}
